import {
  useState,
  useCallback,
  ReactElement,
  ReactNode,
  MouseEventHandler,
  MouseEvent,
  cloneElement,
} from 'react';

import MUITooltip, {
  TooltipProps as MUITooltipProps,
} from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import {
  useIsDesktop,
  useIsInsideContainer,
  useActionOnScroll,
} from '../hooks';

import useStyles from './Tooltip.styles';

/**
 * Свойства компонента {@link Tooltip}
 */
export interface TooltipProps {
  /**
   * Дочерний элемент, к которому привязан тултип.
   */
  children: ReactElement;

  /**
   * Текст заголовка или React-элемент, отображаемый в тултипе.
   */
  title: ReactNode;

  /**
   * Необязательное положение тултипа.
   *
   * @defaultValue `'right'`
   */
  placement?: MUITooltipProps['placement'];

  /**
   * Логическое значение, указывающее, отключен ли тултип.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Логическое значение, указывающее, является ли тултип интерактивным.
   *
   * @defaultValue `false`
   */
  interactive?: boolean;

  /**
   * Необязательная функция обратного вызова, вызываемая при открытии тултипа.
   */
  onOpen?: () => void;

  /**
   * Необязательная функция обратного вызова, вызываемая при закрытии тултипа.
   */
  onClose?: () => void;

  /**
   * Логическое значение, указывающее, следует ли использовать инвертированные стили для тултипа.
   *
   * @defaultValue `false`
   */
  inverted?: boolean;

  /**
   * Максимальная ширина.
   */
  maxWidth?: number | string;

  /**
   * Необязательная задержка (в миллисекундах) перед появлением тултипа.
   */
  enterDelay?: number;

  /**
   * Необязательный параметр для юнит-тестов.
   */
  testId?: string;
}

/**
 * Компонент Tooltip, который отображает тултип для указанного дочернего элемента.
 * Позволяет настраивать заголовок, положение и поведение тултипа.
 *
 * @public
 * @param children - Дочерний элемент, к которому привязан тултип.
 * @param title - Текст заголовка или React-элемент, отображаемый в тултипе.
 * @param placement - Необязательное положение тултипа.
 * @param disabled - Логическое значение, указывающее, отключен ли тултип.
 * @param interactive - Логическое значение, указывающее, является ли тултип интерактивным.
 * @param maxWidth - Максимальная ширина.
 * @param onOpen - Необязательная функция обратного вызова, вызываемая при открытии тултипа.
 * @param onClose - Необязательная функция обратного вызова, вызываемая при закрытии тултипа.
 * @param inverted - Логическое значение, указывающее, следует ли использовать инвертированные стили для тултипа.
 * @param enterDelay - Необязательная задержка (в миллисекундах) перед появлением тултипа.
 * @param testId - Необязательный параметр для юнит-тестов.
 * @returns React-элемент, представляющий тултип.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link TooltipProps}
 */
export const Tooltip = ({
  children,
  title,
  placement = 'right',
  disabled = false,
  interactive = false,
  maxWidth,
  onOpen,
  onClose,
  inverted = false,
  enterDelay,
  testId = 'tooltip-test-id',
}: TooltipProps) => {
  const { classes } = useStyles({ inverted, maxWidth });
  const isDesktop = useIsDesktop();
  const isInsideContainer = useIsInsideContainer();

  const [open, setOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setOpen((prevState) => {
      if (prevState) {
        onClose?.();
      } else {
        onOpen?.();
      }
      return !prevState;
    });
  }, [onOpen, onClose]);

  const handleClickAway = useCallback(() => {
    if (open) {
      setOpen(false);
      onClose?.();
    }
  }, [open, onClose]);

  useActionOnScroll({
    active: !isDesktop && !disabled && open,
    onAction: handleClickAway,
    scrollSelector: isInsideContainer ? null : undefined,
  });

  if (disabled) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  const tooltipProps = {
    classes,
    arrow: true,
    title,
    placement,
    disableInteractive: !interactive,
    enterDelay,
    className: 'aqa_tooltip',
    'data-testid': testId,
  };

  const popperModifiers = [
    {
      name: 'offset',
      options: {
        offset: [0, -4],
      },
    },
  ];

  if (!isDesktop) {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <MUITooltip
          {...tooltipProps}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          slotProps={{
            popper: {
              modifiers: popperModifiers,
            },
          }}
        >
          {cloneElement(
            children as ReactElement<{
              onClick?: MouseEventHandler;
            }>,
            {
              onClick: (e: MouseEvent) => {
                e.stopPropagation();
                (children.props as { onClick?: MouseEventHandler }).onClick?.(
                  e,
                );
                handleToggle();
              },
            },
          )}
        </MUITooltip>
      </ClickAwayListener>
    );
  }

  return (
    <MUITooltip
      {...tooltipProps}
      enterTouchDelay={0}
      onOpen={onOpen}
      onClose={onClose}
      slotProps={{
        popper: {
          modifiers: popperModifiers,
        },
      }}
    >
      {children}
    </MUITooltip>
  );
};
